import { requestScore, type ScoreResponse } from './validate-api';
import {
  scorableTurnIndices,
  type RunScore,
  type ValidateTurn,
} from './validate-runs';

/** A scorer verdict as the run keeps it. */
export const toRunScore = (verdict: ScoreResponse): RunScore => ({
  status: 'scored',
  verdict: {
    scores: verdict.scores,
    autoFail: verdict.autoFail,
    ...(verdict.criteria?.length ? { criteria: verdict.criteria } : {}),
  },
  summary: verdict.summary,
  reasons: verdict.reasons ?? {},
  whyFailed: verdict.whyFailed ?? null,
  toneNote: verdict.toneNote ?? null,
});

const errorScore = (err: unknown): RunScore => ({
  status: 'error',
  message: err instanceof Error ? err.message : 'Scoring failed.',
});

/** Score one turn — or the whole conversation, with `focusIndex: null`. */
export const scoreFocus = async (
  args: {
    transcript: ValidateTurn[];
    focusIndex: number | null;
    scenario?: string;
    expectation?: string;
  },
  {
    score = requestScore,
    signal,
  }: { score?: typeof requestScore; signal?: AbortSignal } = {}
): Promise<RunScore> => {
  try {
    return toRunScore(await score(args, signal));
  } catch (err) {
    return errorScore(err);
  }
};

/**
 * How many turns are scored at once. Each is its own model call; a six-turn
 * scenario scored one at a time would add most of a minute, and scoring all
 * at once would stack every suite case's calls on top of each other.
 */
export const TURN_SCORE_CONCURRENCY = 3;

/**
 * Score every scorable bot reply in a conversation (`scorableTurnIndices`),
 * each in the context of the whole transcript. Resolves with a score per
 * turn — an error score for a turn whose call failed, never a rejection, so
 * one bad call does not lose the rest.
 */
export const scoreTurns = async (
  args: {
    transcript: ValidateTurn[];
    scenario?: string;
    expectation?: string;
  },
  deps: {
    score?: typeof requestScore;
    signal?: AbortSignal;
    onTurn?: (index: number, score: RunScore) => void;
  } = {}
): Promise<Record<string, RunScore>> => {
  const queue = scorableTurnIndices(args.transcript);
  const out: Record<string, RunScore> = {};
  const worker = async () => {
    for (;;) {
      const index = queue.shift();
      if (index === undefined || deps.signal?.aborted) return;
      // eslint-disable-next-line no-await-in-loop
      const result = await scoreFocus({ ...args, focusIndex: index }, deps);
      out[String(index)] = result;
      deps.onTurn?.(index, result);
    }
  };
  await Promise.all(
    Array.from({ length: TURN_SCORE_CONCURRENCY }, () => worker())
  );
  return out;
};
